'use client';

import { X } from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { useBannerStore } from '@/stores/Banner';

import { useAnnouncement } from '../api/getAnnouncement';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

export const GlobalBanner = () => {
  const isBannerVisible = useBannerStore((state) => state.isBannerVisible);
  const setBannerVisible = useBannerStore((state) => state.setBannerVisible);
  const { data: announcement } = useAnnouncement();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });

  useEffect(() => {
    if (!announcement?.endTimestamp) return;

    const calculateTimeLeft = () => {
      const now = moment().valueOf();
      const endDateInMs = announcement.endTimestamp * 1000;
      const difference = endDateInMs - now;

      if (difference <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        });
        return;
      }

      const duration = moment.duration(difference);
      const days = Math.floor(duration.asDays());
      const hours = Math.floor(duration.asHours()) % 24;
      const minutes = Math.floor(duration.asMinutes()) % 60;
      const seconds = Math.floor(duration.asSeconds()) % 60;

      setTimeLeft({
        days,
        hours,
        minutes,
        seconds,
        isExpired: false,
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [announcement]);

  useEffect(() => {
    if (announcement?.isActive) {
      if (announcement.isClosable) {
        const dismissed = localStorage.getItem(
          `announcementDismissed_${announcement.id}`,
        );
        setBannerVisible(!dismissed);
      } else {
        setBannerVisible(true);
      }
    }
  }, [announcement, setBannerVisible]);

  const handleDismiss = () => {
    if (announcement && announcement.isClosable) {
      localStorage.setItem(`announcementDismissed_${announcement.id}`, 'true');
      setBannerVisible(false);
    }
  };

  if (!isBannerVisible || !announcement?.isActive || timeLeft.isExpired)
    return null;

  return (
    <div className="bg-card py-3 text-foreground drop-shadow-xl">
      <div className="mx-auto flex items-center gap-4 sm:px-6 md:px-8">
        <div className="flex grow flex-col gap-2 md:flex-row md:items-center">
          <div className="grow space-y-0.5">
            <p className="text-base">
              <span className="font-medium">{announcement.type}: </span>
              {announcement.link ? (
                <Link
                  to={announcement.link}
                  className="font-semibold text-primary hover:text-primary/90"
                  target={announcement.openInNewTab ? '_blank' : '_self'}
                >
                  {announcement.message}
                </Link>
              ) : (
                <span>{announcement.message}</span>
              )}
            </p>
          </div>

          {/* Countdown and Action Button */}
          {announcement.endTimestamp && (
            <div className="flex items-center gap-3">
              <div className="flex items-center divide-x rounded-md bg-primary/15 text-sm">
                {timeLeft.days > 0 && (
                  <span className="px-2 py-1">
                    {timeLeft.days}
                    <span className="text-muted-foreground">d</span>
                  </span>
                )}
                <span className="px-2 py-1">
                  {timeLeft.hours.toString().padStart(2, '0')}
                  <span className="text-muted-foreground">h</span>
                </span>
                <span className="px-2 py-1">
                  {timeLeft.minutes.toString().padStart(2, '0')}
                  <span className="text-muted-foreground">m</span>
                </span>
                <span className="px-2 py-1">
                  {timeLeft.seconds.toString().padStart(2, '0')}
                  <span className="text-muted-foreground">s</span>
                </span>
              </div>
              {announcement.actionText && announcement.link && (
                <Link
                  to={announcement.link}
                  target={announcement.openInNewTab ? '_blank' : '_self'}
                >
                  <Button size="sm" className="h-7">
                    {announcement.actionText}
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Close Button - only if closable */}
        {announcement.isClosable && (
          <button
            onClick={handleDismiss}
            className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close banner"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};
